import { api } from './api';
import { LoginCredentials, RegisterData, AuthResponse, ApiResponse } from '../types';

// Request cache and deduplication
interface RequestCache {
  [key: string]: {
    promise: Promise<any>;
    timestamp: number;
    resolve?: (value: any) => void;
    reject?: (reason: any) => void;
  };
}

const requestCache: RequestCache = {};
const CACHE_TTL = 10000; // 10 seconds

class AuthService {
  // Login user
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    // Clear any cached requests before new login
    Object.keys(requestCache).forEach(key => delete requestCache[key]);

    const response = await api.post<AuthResponse>('/auth/login', credentials);

    // Store tokens and user data
    if (response.success && response.data) {
      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response;
  }

  // Register new user
  async register(userData: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const response = await api.post<AuthResponse>('/auth/register', userData);

    // Store tokens and user data
    if (response.success && response.data) {
      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response;
  }

  // Login with Google
  async loginWithGoogle(idToken: string): Promise<ApiResponse<AuthResponse>> {
    const response = await api.post<AuthResponse>('/auth/google-login', { idToken });

    // Store tokens and user data
    if (response.success && response.data) {
      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response;
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Clear any cached requests
      Object.keys(requestCache).forEach(key => delete requestCache[key]);

      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  // Get current user profile
  async getCurrentUser(): Promise<ApiResponse> {
    const cacheKey = 'get-current-user';
    const now = Date.now();

    // Check if we have a cached request
    if (requestCache[cacheKey]) {
      const cached = requestCache[cacheKey];

      // If cache is still valid and request is in progress, return the existing promise
      if (now - cached.timestamp < CACHE_TTL && cached.promise) {
        console.log('Returning cached getCurrentUser promise');
        return cached.promise;
      }

      // Clean up expired cache
      delete requestCache[cacheKey];
    }

    // Create new request with caching
    const promise = api.get('/auth/me')
      .finally(() => {
        // Clean up cache after request completes (with delay to allow concurrent requests to join)
        setTimeout(() => {
          if (requestCache[cacheKey] && requestCache[cacheKey].promise === promise) {
            delete requestCache[cacheKey];
          }
        }, 1000);
      });

    // Cache the promise
    requestCache[cacheKey] = {
      promise,
      timestamp: now,
    };

    console.log('Created new getCurrentUser request with caching');
    return promise;
  }

  // Refresh access token
  async refreshToken(): Promise<ApiResponse<{ accessToken: string }>> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<{ accessToken: string }>('/auth/refresh', {
      refreshToken,
    });

    if (response.success && response.data) {
      localStorage.setItem('accessToken', response.data.accessToken);
    }

    return response;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    return !!token;
  }

  // Get stored user data
  getStoredUser(): any {
    const userStr = localStorage.getItem('user');
    if (!userStr || userStr === 'undefined' || userStr === 'null') {
      return null;
    }
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Failed to parse stored user:', error);
      return null;
    }
  }

  // Get stored access token
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  // Get stored refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  // Update user role
  async updateUserRole(role: string): Promise<ApiResponse<AuthResponse>> {
    const response = await api.put<AuthResponse>('/auth/update-role', { role });

    // Update stored user data
    if (response.success && response.data) {
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response;
  }
}

export const authService = new AuthService();